import React, { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  Clock,
  Link as LinkIcon,
  X,
  ChevronDown,
  ChevronRight,
  AlertTriangle,
  Plus,
  Search,
} from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

/**
 * Worksection per-task section for TaskDetailPage (/tasks/{id}).
 *
 * Shows a Phase 3-preview cost rollup for this portal task based on its linked
 * Worksection tasks: aggregated hours × portal-staff salary/160 hourly rate.
 *
 * READ-ONLY display — does NOT write to task.staff_cost_total. Real auto-write
 * is gated behind a future global toggle (worksection_apply_cost, off by default).
 *
 * Actions:
 *   - View total hours/cost + per-WS-task and per-user breakdowns
 *   - Add more WS tasks (picker scoped to the WS project linked to the parent portal project)
 *   - Unlink individual WS tasks
 *
 * Collapsed by default — header shows status at a glance, expand on demand.
 */
const WorksectionTaskSection = ({ task, canEdit }) => {
  const taskId = task?.id;
  const projectId = task?.project_id;
  // Expanded by default on TaskDetailPage — per-task WS lists are typically short
  // (~5 entries max for a single portal task), so showing the rollup up-front
  // is more useful than hiding it. Compare with WorksectionProjectSection which
  // is collapsed by default because it lists ALL ws_tasks of the project.
  const [expanded, setExpanded] = useState(true);
  const [loading, setLoading] = useState(true);
  const [rollup, setRollup] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [pickerSearch, setPickerSearch] = useState("");
  const [adding, setAdding] = useState(false);
  const [projectLinked, setProjectLinked] = useState(null); // null=unknown, true/false after check

  const loadRollup = useCallback(async () => {
    if (!taskId) return;
    setLoading(true);
    try {
      const res = await api.get(`/worksection/task-rollup/${taskId}`);
      setRollup(res.data);
    } catch (e) {
      console.warn("worksection rollup fetch failed:", e?.response?.status);
      setRollup(null);
    } finally {
      setLoading(false);
    }
  }, [taskId]);

  const loadProjectLinkStatus = useCallback(async () => {
    if (!projectId) return;
    try {
      const res = await api.get(`/worksection/project-links/portal/${projectId}`);
      const has = !!(res.data?.id || (res.data?.links?.length || 0) > 0);
      setProjectLinked(has);
    } catch (e) {
      setProjectLinked(false);
    }
  }, [projectId]);

  const loadSuggestions = useCallback(async () => {
    if (!taskId) return;
    try {
      const res = await api.get(`/worksection/task-links/portal/${taskId}/suggestions?limit=20`);
      setSuggestions(res.data || []);
    } catch (e) {
      console.warn("ws task suggestions failed:", e?.response?.status);
      setSuggestions([]);
    }
  }, [taskId]);

  useEffect(() => {
    loadRollup();
    loadProjectLinkStatus();
  }, [loadRollup, loadProjectLinkStatus]);

  useEffect(() => {
    if (pickerOpen) loadSuggestions();
  }, [pickerOpen, loadSuggestions]);

  const handleLinkTask = async (wsTaskId, wsTaskName) => {
    setAdding(true);
    try {
      await api.post("/worksection/task-links", {
        portal_task_id: taskId,
        ws_task_id: wsTaskId,
      });
      toast.success(`Linked Worksection task "${wsTaskName.slice(0, 40)}"`);
      setPickerOpen(false);
      setPickerSearch("");
      await loadRollup();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Link failed");
    } finally {
      setAdding(false);
    }
  };

  const handleUnlinkByWsTask = async (wsTaskId, wsTaskName) => {
    // Find link id (we don't have it in rollup, fetch from task-links)
    try {
      const linksRes = await api.get(`/worksection/task-links/portal/${taskId}`);
      const link = (linksRes.data || []).find((l) => l.ws_task_id === wsTaskId);
      if (!link) {
        toast.error("Link not found");
        return;
      }
      if (!window.confirm(`Unlink "${wsTaskName.slice(0, 40)}" from this task?`)) return;
      await api.delete(`/worksection/task-links/${link.id}`);
      toast.success("Unlinked");
      await loadRollup();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unlink failed");
    }
  };

  if (loading && !rollup) {
    return (
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader className="pb-2">
          <CardTitle className="text-zinc-50 flex items-center gap-2 text-base">
            <Clock className="h-4 w-4 text-emerald-400" />
            Worksection
            <span className="text-xs text-zinc-500">loading…</span>
          </CardTitle>
        </CardHeader>
      </Card>
    );
  }

  const wsCount = rollup?.ws_task_count || 0;
  const totalHours = rollup?.total_hours || 0;
  const totalCost = rollup?.total_cost_usd || 0;
  const userCount = (rollup?.users || []).length;
  const unmapped = rollup?.unmapped_hours || 0;

  // If no project link AND no WS tasks linked here, show a hint and bail
  const noLinks = wsCount === 0;
  const showProjectHint = noLinks && projectLinked === false;

  // Suggestions excluding already-linked WS tasks for this portal task
  const linkedWsIds = new Set((rollup?.ws_tasks || []).map((t) => t.ws_task_id));
  const availableSuggestions = suggestions
    .filter((s) => !linkedWsIds.has(s.ws_task_id))
    .filter((s) => !pickerSearch || (s.ws_name || "").toLowerCase().includes(pickerSearch.toLowerCase()));

  return (
    <Card className="bg-zinc-900/50 border-zinc-800">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="flex items-center gap-2 text-zinc-50 text-base font-semibold hover:text-emerald-300 transition-colors"
        >
          {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          <Clock className="h-4 w-4 text-emerald-400" />
          Worksection
          {wsCount > 0 ? (
            <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30 text-[10px] uppercase tracking-wide ml-1">
              {wsCount} task{wsCount === 1 ? "" : "s"} · {totalHours.toFixed(1)}h · ${totalCost.toFixed(2)}
            </Badge>
          ) : showProjectHint ? (
            <Badge className="bg-zinc-800 text-zinc-400 border-zinc-700 text-[10px] uppercase tracking-wide ml-1">
              Project not linked
            </Badge>
          ) : (
            <Badge className="bg-zinc-800 text-zinc-400 border-zinc-700 text-[10px] uppercase tracking-wide ml-1">
              No WS tasks linked
            </Badge>
          )}
        </button>
        {expanded && canEdit && !showProjectHint && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPickerOpen((v) => !v)}
            className="border-zinc-700 h-7 text-xs"
            disabled={adding}
          >
            <Plus className="h-3.5 w-3.5 mr-1" />
            Add WS task
          </Button>
        )}
      </CardHeader>

      {!expanded ? null : (
        <CardContent className="pt-0 space-y-3">
          {showProjectHint && (
            <div className="text-xs text-zinc-400 p-3 bg-amber-500/5 border border-amber-500/20 rounded">
              The parent project isn't linked to a Worksection project yet. Open the project and link it first
              (Worksection section above the Tasks block) — then come back here to add individual task links.
            </div>
          )}

          {noLinks && !showProjectHint && (
            <div className="text-xs text-zinc-400 p-3 bg-zinc-950/40 border border-zinc-800 rounded">
              No Worksection tasks linked to this portal task yet. Click <strong>Add WS task</strong> above to pick
              from the linked Worksection project. After linking, time spent by salaried team (designers/developers/testers)
              will roll up here as cost preview.
            </div>
          )}

          {wsCount > 0 && rollup && (
            <>
              {/* Top-level summary cards */}
              <div className="grid grid-cols-3 gap-2">
                <SummaryCard label="Total time" value={`${totalHours.toFixed(1)}h`} color="emerald" />
                <SummaryCard
                  label="Cost (USD)"
                  value={`$${totalCost.toFixed(2)}`}
                  color="cyan"
                  hint="hours × portal staff salary/160"
                />
                <SummaryCard label="Contributors" value={userCount} color="violet" />
              </div>

              {unmapped > 0 && (
                <div className="text-[11px] text-amber-400 flex items-center gap-1.5">
                  <AlertTriangle className="h-3 w-3" />
                  {unmapped.toFixed(1)}h logged by Worksection users not linked to portal staff (cost not computed for them).
                  Go to <a href="/settings/worksection/users" className="underline">/settings/worksection/users</a> to map them.
                </div>
              )}

              {/* Per WS task breakdown */}
              <div className="border border-zinc-800 rounded overflow-hidden">
                <div className="px-3 py-1.5 bg-zinc-950 border-b border-zinc-800 text-[11px] uppercase tracking-wide text-zinc-500">
                  By Worksection task
                </div>
                {rollup.ws_tasks.map((t) => (
                  <div
                    key={t.ws_task_id}
                    className="px-3 py-2 border-b border-zinc-800/60 last:border-0 flex items-center gap-3"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-zinc-200 truncate">{t.ws_task_name || `Task ${t.ws_task_id}`}</div>
                      <div className="text-[11px] text-zinc-500">
                        {t.entries_count} time {t.entries_count === 1 ? "entry" : "entries"}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-zinc-200 font-mono text-sm">{t.hours.toFixed(2)}h</div>
                      <div className="text-emerald-300 font-mono text-[11px]">${t.cost_usd.toFixed(2)}</div>
                    </div>
                    {canEdit && (
                      <button
                        type="button"
                        onClick={() => handleUnlinkByWsTask(t.ws_task_id, t.ws_task_name)}
                        className="text-zinc-500 hover:text-red-400"
                        title="Unlink"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                ))}
              </div>

              {/* Per user breakdown */}
              <div className="border border-zinc-800 rounded overflow-hidden">
                <div className="px-3 py-1.5 bg-zinc-950 border-b border-zinc-800 text-[11px] uppercase tracking-wide text-zinc-500">
                  By contributor (hourly rate from portal salary)
                </div>
                {rollup.users.map((u) => (
                  <div
                    key={u.ws_user_id}
                    className="px-3 py-2 border-b border-zinc-800/60 last:border-0 grid grid-cols-12 gap-2 items-center"
                  >
                    <div className="col-span-5 min-w-0">
                      <div className="text-sm text-zinc-200 truncate">{u.portal_staff_name || u.ws_user_name}</div>
                      {u.portal_staff_name && u.ws_user_name && u.portal_staff_name !== u.ws_user_name && (
                        <div className="text-[11px] text-zinc-500 truncate">WS: {u.ws_user_name}</div>
                      )}
                    </div>
                    <div className="col-span-2 text-right text-zinc-300 font-mono text-sm">
                      {u.hours.toFixed(2)}h
                    </div>
                    <div className="col-span-2 text-right text-zinc-500 text-[11px]">
                      × ${u.rate_per_hour_usd.toFixed(2)}/h
                    </div>
                    <div className="col-span-3 text-right text-emerald-300 font-mono text-sm">
                      = ${u.cost_usd.toFixed(2)}
                    </div>
                  </div>
                ))}
              </div>

              <div className="text-[11px] text-zinc-500 italic">
                Phase 3-preview: cost is shown here as reference only and does <strong>not</strong> write into{" "}
                <code className="bg-zinc-800 px-1 rounded">task.staff_cost_total</code>. A future global toggle will
                enable auto-write to portal cost.
              </div>
            </>
          )}

          {pickerOpen && canEdit && (
            <div className="border border-zinc-700 rounded bg-zinc-950">
              <div className="p-2 border-b border-zinc-800 flex items-center gap-2">
                <Search className="h-3.5 w-3.5 text-zinc-500" />
                <input
                  autoFocus
                  type="text"
                  placeholder="Search Worksection tasks in this project…"
                  value={pickerSearch}
                  onChange={(e) => setPickerSearch(e.target.value)}
                  className="bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-xs flex-1"
                />
                <button
                  type="button"
                  onClick={() => setPickerOpen(false)}
                  className="text-zinc-500 hover:text-zinc-300"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
              <div className="max-h-72 overflow-y-auto">
                {availableSuggestions.length === 0 ? (
                  <div className="p-3 text-xs text-zinc-500 italic">
                    {suggestions.length === 0
                      ? "No Worksection tasks available — project may not be linked, or all tasks already linked."
                      : "No matches"}
                  </div>
                ) : (
                  availableSuggestions.map((s) => {
                    const isClosed = s.ws_status === "done";
                    return (
                      <button
                        key={s.ws_task_id}
                        type="button"
                        onClick={() => handleLinkTask(s.ws_task_id, s.ws_name)}
                        disabled={adding}
                        className="w-full text-left px-3 py-2 hover:bg-zinc-800 border-b border-zinc-800/60 last:border-0 flex items-center gap-2"
                      >
                        <LinkIcon className="h-3.5 w-3.5 text-emerald-400 shrink-0" />
                        <span className="text-sm text-zinc-200 truncate flex-1">{s.ws_name || `Task ${s.ws_task_id}`}</span>
                        <Badge className={`text-[10px] ${isClosed
                          ? "bg-zinc-700/40 text-zinc-400 border-zinc-700"
                          : "bg-cyan-500/15 text-cyan-300 border-cyan-500/30"
                        }`}>{s.ws_status || "?"}</Badge>
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
};

// ---------- small subcomponent ----------

const SummaryCard = ({ label, value, color, hint }) => {
  const colorMap = {
    emerald: "border-emerald-500/30",
    cyan: "border-cyan-500/30",
    violet: "border-violet-500/30",
  };
  return (
    <div className={`bg-zinc-950/40 border rounded p-2 ${colorMap[color] || "border-zinc-800"}`} title={hint || ""}>
      <div className="text-[10px] uppercase tracking-wide text-zinc-500">{label}</div>
      <div className="text-lg font-semibold text-zinc-100">{value}</div>
    </div>
  );
};

export default WorksectionTaskSection;
